import { sample } from 'effector';
import { debounce, delay } from 'patronum';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import { AlertProps } from '@mui/material';
import { MOUNT_DELAY } from '@consts/delay';
import { signout } from '@features/common';
import i18n from '@shared/config/i18n';
import { processStatuses } from '../../types';
import { setToastData } from '../toast/model';
import {
  $notReadImports,
  $openImportsPopperCounter,
  CHECK_NOT_READ_STATUS_TIMEOUT,
  fxExportInterrupt,
  fxImportReadNotification,
  fxGetActiveExports,
  exportInterrupt,
  importReadNotification,
  exportCheckNotReadStatus,
  ImportTemporalGate,
  downloadFile,
  fxDownloadFile,
} from './model';

const { t } = i18n;

$openImportsPopperCounter
  .on(fxGetActiveExports.done, (state) => state + 1)
  .reset(signout);

$notReadImports
  .on(fxGetActiveExports.doneData, (_, { processes }) => processes)
  .reset(signout);

/** Проверяю наличие активных импортов после запроса и,
 * если есть, то запускаю событие через TIMEOUT */
const isActiveImport = sample({
  clock: debounce({
    source: fxGetActiveExports.doneData,
    timeout: CHECK_NOT_READ_STATUS_TIMEOUT,
  }),
  filter: ({ processes }) =>
    !!processes.filter(({ status }) => status === processStatuses.processing).length,
});
/** Проверяю наличие активных импортов после ручного запроса (отправки файла на импорт) и,
 * если есть, то запускаю событие через TIMEOUT */
const isActiveManualyImport = sample({
  clock: debounce({
    source: fxGetActiveExports.doneData,
    timeout: CHECK_NOT_READ_STATUS_TIMEOUT,
  }),
  filter: ({ processes }) =>
    !!processes.filter(({ status }) => status === processStatuses.processing).length,
});

/** Стартую проверку активных импортов при загрузке
 * и после получения этих же данных с импортами в обработке */
const delayedOpen = delay({
  source: ImportTemporalGate.open,
  timeout: MOUNT_DELAY,
});

sample({
  clock: [
    delayedOpen,
    isActiveImport,
    isActiveManualyImport,
    fxExportInterrupt.done,
    fxImportReadNotification.done,
  ],
  target: fxGetActiveExports,
});
/** Стартую проверку активных импортов */
sample({
  clock: exportCheckNotReadStatus,
  target: fxGetActiveExports,
});

/** Прерываю импорт */
sample({
  clock: exportInterrupt,
  fn: (process_id) => ({ process_id }),
  target: fxExportInterrupt,
});

/** Отправляю запрос, что импорты прочтены */
sample({
  clock: importReadNotification,
  fn: (process_ids) => ({ process_ids }),
  target: fxImportReadNotification,
});

/** Скачивание файла */
sample({
  clock: downloadFile,
  target: fxDownloadFile,
});

sample({
  clock: [fxGetActiveExports.fail, fxExportInterrupt.fail, fxImportReadNotification.fail],
  fn: ({ error }) => ({
    visible: true,
    type: 'error' as AlertProps['severity'],
    text: error?.message ?? t('UnexpectedError'),
    autoHideTimerMs: 5000,
  }),
  target: setToastData,
});

fxDownloadFile.done.watch(({ params: { fileName }, result }) => {
  const blob = new Blob([result], {
    type: 'application/octet-stream',
  });

  const dateTimeNow = format(new Date(), 'dd.MM.yyyy-HH.mm.ss');
  FileSaver.saveAs(blob, `${fileName}-${dateTimeNow}.xlsx`);
});
